/**
 * Time formatting and parsing utilities
 */

import { FrequencyAdaptiveConfig } from './FrequencyAdaptiveConfig'
import { MS_PER_MINUTE, MS_PER_S, SECONDS_PER_MINUTE } from './timeUnits'

/**
 * Parses a time string "mm:ss", with any fraction of a second, to milliseconds
 * @param timeString Time string (e.g., "01:23.4")
 * @returns Time in milliseconds
 */
export function parseTimeToMs(timeString: string): number {
  const parts = timeString.split(':')
  if (parts.length !== 2) return 0

  const minutes = parseInt(parts[0], 10)
  const seconds = parseFloat(parts[1])

  return Math.round((minutes * SECONDS_PER_MINUTE + seconds) * MS_PER_S)
}

export const ELAPSED_TIME_LABEL_STEP_MS = 100

export function formatElapsedTime(ms: number): string {
  const minutes = Math.floor(ms / MS_PER_MINUTE)
  const seconds = Math.floor((ms % MS_PER_MINUTE) / MS_PER_S)
  const tenths = Math.floor((ms % MS_PER_S) / ELAPSED_TIME_LABEL_STEP_MS)

  return `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}.${tenths}`
}

/**
 * Formats a timespan with customizable granularity
 * @param ms Time in milliseconds
 * @param options Formatting options
 */
export function formatTimeSpan(
  ms: number,
  options?: {
    granularity?: 'seconds' | 'tenths' | 'hundredths' | 'milliseconds'
    format?: 'mm:ss.S' | 'mm:ss.SS' | 'mm:ss.SSS' | 'ss.S'
    decimals?: number
  }
): string {
  const { granularity = 'tenths', format = 'mm:ss.S', decimals } = options || {}

  // Round based on granularity
  let roundedMs: number
  if (granularity === 'seconds') {
    roundedMs = Math.round(ms / 1000) * 1000
  } else if (granularity === 'tenths') {
    roundedMs = Math.round(ms / 100) * 100
  } else if (granularity === 'hundredths') {
    roundedMs = Math.round(ms / 10) * 10
  } else {
    // milliseconds - no rounding
    roundedMs = ms
  }

  const minutes = Math.floor(roundedMs / MS_PER_MINUTE)
  const seconds = Math.floor((roundedMs % MS_PER_MINUTE) / MS_PER_S)
  const milliseconds = roundedMs % MS_PER_S

  if (format === 'ss.S') {
    const totalSeconds = Math.floor(ms / MS_PER_S)
    const tenthsUnrounded = Math.floor((ms % MS_PER_S) / 100)
    return `${totalSeconds}.${tenthsUnrounded}`
  }

  if (granularity === 'seconds') {
    return `${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`
  }

  // Format milliseconds with optional padding
  let millisecondsStr: string
  if (decimals !== undefined) {
    // Pad to specified number of decimals
    millisecondsStr = milliseconds.toString().padStart(decimals, '0')
  } else {
    // No padding - just show the actual precision based on granularity
    if (granularity === 'tenths') {
      millisecondsStr = Math.floor(milliseconds / 100).toString()
    } else if (granularity === 'hundredths') {
      millisecondsStr = Math.floor(milliseconds / 10)
        .toString()
        .padStart(2, '0')
    } else {
      // milliseconds
      millisecondsStr = milliseconds.toString().padStart(3, '0')
    }
  }

  return `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}.${millisecondsStr}`
}

/**
 * Rounds time to nearest interval for indexing
 * @param timeMs Time in milliseconds
 * @param intervalMs Interval in milliseconds (default 100)
 * @returns Rounded time
 */
export function roundTime(timeMs: number, intervalMs: number = 100): number {
  return Math.round(timeMs / intervalMs) * intervalMs
}

/**
 * Calculate sampling frequency from telemetry data points.
 *
 * This is the SINGLE SOURCE OF TRUTH for GPS frequency detection.
 * All frequency-dependent algorithms should use this function.
 *
 * @param points - Array of points with elapsedMs property
 * @param defaultHz - Default frequency if calculation fails (default: 10)
 * @returns Sampling frequency in Hz
 */
export function calculateSamplingFrequencyHz(
  points: Array<{ elapsedMs: number }>,
  defaultHz: number = 10
): number {
  if (points.length < 2) {
    return defaultHz
  }

  const totalDurationMs =
    points[points.length - 1].elapsedMs - points[0].elapsedMs

  if (totalDurationMs <= 0) {
    return defaultHz
  }

  return (points.length / totalDurationMs) * MS_PER_S
}

/**
 * Get frequency-adaptive configuration for smoothing algorithms.
 *
 * Provides consistent parameters across all processors that need to
 * adapt their behavior based on GPS sampling frequency.
 *
 * Device frequencies:
 * - AIM: ~10Hz
 * - Racebox: ~25Hz
 * - PZRacing: ~50Hz
 *
 * @param frequencyHz - The detected sampling frequency
 * @returns Configuration object with frequency-adaptive parameters
 */
export function getFrequencyAdaptiveConfig(
  frequencyHz: number
): FrequencyAdaptiveConfig {
  // Scale factor: normalize to 10Hz baseline
  // Most smoothing parameters are tuned for ~10Hz, scale up for higher frequencies
  const frequencyScale = Math.max(1, frequencyHz / 10)

  // Number of smoothing passes based on frequency:
  // - ≤15Hz (AIM): Single pass is sufficient, data is already relatively smooth
  // - 15-25Hz (Racebox): Two passes to handle moderate noise
  // - 25-40Hz: Four passes for high-frequency data
  // - >40Hz (PZRacing 50Hz): Five passes needed for noisier high-frequency data
  //
  // Higher frequencies capture more fine-grained noise which gets amplified
  // when differentiating speed to get acceleration.
  let smoothingPasses: number
  if (frequencyHz > 40) {
    smoothingPasses = 5
  } else if (frequencyHz > 25) {
    smoothingPasses = 4
  } else if (frequencyHz > 15) {
    smoothingPasses = 2
  } else {
    smoothingPasses = 1
  }

  return {
    frequencyHz,
    frequencyScale,
    smoothingPasses,
  }
}
